import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MdExpandMore, MdFlashOn, MdShield, MdWarning } from 'react-icons/md';
import useOnClickOutside from '@hooks/useOnClickOutside';
import { type ApprovalMode } from '@utils/approvalMode';
import {
  ApprovalPickerContainer,
  ApprovalPickerTrigger,
  ApprovalPickerMenu,
  ApprovalPickerItem,
  ApprovalPickerItemContent,
  ApprovalPickerItemLabel,
  ApprovalPickerItemDesc,
} from './Chat.styled';

interface ApprovalModePickerProps {
  approvalMode: ApprovalMode;
  onPick: (mode: ApprovalMode) => void;
  disabled?: boolean;
  menuDirection?: 'up' | 'down';
}

const ApprovalModePicker: React.FC<ApprovalModePickerProps> = ({ approvalMode, onPick, disabled, menuDirection = 'up' }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const ref = useOnClickOutside<HTMLDivElement>(() => setOpen(false));

  const handlePick = (mode: ApprovalMode) => {
    onPick(mode);
    setOpen(false);
  };

  return (
    <ApprovalPickerContainer ref={ref}>
      <ApprovalPickerTrigger
        type="button"
        $warn={approvalMode === 'run_all'}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        data-testid="approval-mode-picker"
        title={
          approvalMode === 'run_all'
            ? intl.formatMessage({ id: 'chat.approval.runAll.title', defaultMessage: 'Warning: all tools run without approval' })
            : intl.formatMessage({ id: 'chat.approval.requireApproval.title', defaultMessage: 'Require approval for code execution' })
        }
      >
        {approvalMode === 'run_all' ? <MdWarning /> : <MdShield />}
        {approvalMode === 'run_all'
          ? intl.formatMessage({ id: 'chat.approval.runAll.label', defaultMessage: 'Run everything' })
          : intl.formatMessage({ id: 'chat.approval.requireApproval.label', defaultMessage: 'Require approval' })}
        <MdExpandMore />
      </ApprovalPickerTrigger>
      {open && (
        <ApprovalPickerMenu $menuDirection={menuDirection}>
          <ApprovalPickerItem
            $selected={approvalMode === 'require_approval'}
            onClick={() => handlePick('require_approval')}
          >
            <MdShield />
            <ApprovalPickerItemContent>
              <ApprovalPickerItemLabel>
                {intl.formatMessage({ id: 'chat.approval.requireApproval.label', defaultMessage: 'Require approval' })}
              </ApprovalPickerItemLabel>
              <ApprovalPickerItemDesc>
                {intl.formatMessage({ id: 'chat.approval.requireApproval.desc', defaultMessage: 'Ask before running code or processes' })}
              </ApprovalPickerItemDesc>
            </ApprovalPickerItemContent>
          </ApprovalPickerItem>
          <ApprovalPickerItem
            $selected={approvalMode === 'run_all'}
            $warn={true}
            onClick={() => handlePick('run_all')}
          >
            <MdFlashOn />
            <ApprovalPickerItemContent>
              <ApprovalPickerItemLabel>
                {intl.formatMessage({ id: 'chat.approval.runAll.label', defaultMessage: 'Run everything' })}
              </ApprovalPickerItemLabel>
              <ApprovalPickerItemDesc>
                {intl.formatMessage({ id: 'chat.approval.runAll.desc', defaultMessage: 'Execute all tools immediately — use with caution' })}
              </ApprovalPickerItemDesc>
            </ApprovalPickerItemContent>
          </ApprovalPickerItem>
        </ApprovalPickerMenu>
      )}
    </ApprovalPickerContainer>
  );
};

export default ApprovalModePicker;
